/* =========================================================================
   telemetry-stream-helpers.ts — pure helper functions for WebSocket real-time
   telemetry payload normalization and series data merging.
   ========================================================================= */

import { TelemetrySeries, TelemetryDataPoint } from './telemetry-types';
import { mergeAndSortTelemetryData } from '../../../../../CommonComponents/telemetry/utils/telemetry-helpers.utils';

/**
 * Normalizes raw WebSocket frames (arrays, single series objects, wrapper objects, or single point payloads)
 * into a clean TelemetrySeries[] array matching TypeScript strict types.
 */
export function normalizeWsPayload(rawData: any): TelemetrySeries[] {
  if (!rawData) return [];

  if (Array.isArray(rawData)) {
    return rawData.filter((item): item is TelemetrySeries => item && typeof item === 'object' && !!item.id && Array.isArray(item.data));
  }

  if (typeof rawData === 'object' && rawData !== null) {
    if (rawData.id && Array.isArray(rawData.data)) {
      return [rawData as TelemetrySeries];
    }
    if (Array.isArray(rawData.data)) {
      return rawData.data.filter((item: any): item is TelemetrySeries => item && typeof item === 'object' && !!item.id && Array.isArray(item.data));
    }
    if (rawData.id) {
      const ptValue = rawData.value ?? rawData.data;
      if (ptValue !== undefined && ptValue !== null) {
        const rawTime = rawData.timestamp || Date.now();
        const ptTime = rawTime < 1e11 ? rawTime * 1000 : rawTime;
        const dp: TelemetryDataPoint = {
          value: (Array.isArray(ptValue) && typeof ptValue[0] === 'number'
            ? (ptValue[0] < 1e11 ? [ptValue[0] * 1000, ptValue[1]] : ptValue)
            : [ptTime, ptValue]) as [number, number | number[]],
        };
        return [{ id: rawData.id, data: [dp] }];
      }
    }
  }

  return [];
}

/**
 * Merges incoming live WebSocket telemetry series with current series state.
 * Ensures incoming live stream points have timestamps >= latest existing time,
 * placing live packets at the array tail for real-time KPI card and panel updates.
 */
export function mergeWsIncomingSeries(
  currentSeriesList: TelemetrySeries[],
  incomingSeriesList: TelemetrySeries[]
): { updatedSeriesList: TelemetrySeries[]; hasUpdates: boolean } {
  let hasUpdates = false;
  const updatedSeriesList = [...currentSeriesList];

  incomingSeriesList.forEach((inc) => {
    if (!inc || !inc.id || !Array.isArray(inc.data) || inc.data.length === 0) return;

    const existingIdx = updatedSeriesList.findIndex((s) => s && s.id === inc.id);

    if (existingIdx >= 0) {
      const existingSeries = updatedSeriesList[existingIdx];
      const existingData = existingSeries?.data || [];
      const maxExistingTime = existingData?.length > 0
        ? existingData[existingData.length - 1]?.value[0]
        : 0;

      // Normalize incoming timestamps from seconds to milliseconds
      const normalizedIncData = inc.data
        .map((dp) => {
          const rawT = dp.value[0];
          const normT = rawT < 1e11 ? rawT * 1000 : rawT;
          return { ...dp, value: [normT, dp.value[1]] as [number, number | number[]] };
        })
        .filter((dp) => {
          // Drop points with timestamps already covered by existing data.
          // mergeAndSortTelemetryData handles near-duplicate deduplication (5s threshold),
          // here we only skip clearly stale or duplicate points to preserve timestamp accuracy.
          return maxExistingTime <= 0 || dp.value[0] > maxExistingTime;
        });

      if (normalizedIncData.length > 0) {
        const mergedPoints = mergeAndSortTelemetryData(existingData, normalizedIncData, 5000);
        hasUpdates = true;
        updatedSeriesList[existingIdx] = {
          ...existingSeries,
          data: mergedPoints,
        };
      }
    } else {
      // Append new incoming series if not already in list
      hasUpdates = true;
      updatedSeriesList.push(inc);
    }
  });

  return { updatedSeriesList, hasUpdates };
}
